import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Navigation: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { href: '/', label: 'Inicio', isExternal: true },
    { 
      href: '/servicios', 
      label: 'Servicios',
      isExternal: true,
      submenu: [
        { href: '/servicios/catering', label: 'Catering Marino' },
        { href: '/servicios/eventos', label: 'Eventos Privados' },
        { href: '/servicios/menu-degustacion', label: 'Menú Degustación' },
        { href: '/servicios/consultoria', label: 'Consultoría Gastronómica' }
      ]
    },
    { href: '/productos', label: 'Productos', isExternal: true },
    { href: '/nosotros', label: 'Nosotros', isExternal: true },
    { href: '/precios', label: 'Catálogo', isExternal: true }
  ];

  return (
    <motion.div 
      className="fixed top-0 w-full z-50 px-3 sm:px-6 lg:px-8 pt-2 md:pt-4"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {/* Layout móvil optimizado */}
      <div className="md:hidden flex items-center justify-between py-2">
        {/* Logo compacto para móvil */}
        <motion.div 
          className="z-60 group"
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 400, damping: 15 }}
        >
          <Link to="/" className="flex items-center gap-2">
            <div className="relative">
              <div className="w-10 h-10 relative overflow-hidden rounded-2xl shadow-lg transition-all duration-500 bg-gradient-to-br from-white/95 via-white/90 to-white/85 backdrop-blur-xl border border-white/40">
                <img 
                  src="/images/icon.jpg" 
                  alt="Mar de Jade Logo" 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
            </div>
            <motion.h1 
              className="text-lg font-title font-bold bg-gradient-to-r from-ocean-500 via-ocean-600 to-jade-500 bg-clip-text text-transparent text-nav-logo"
            >
              Mar de Jade
            </motion.h1>
          </Link>
        </motion.div>

        {/* Menú hamburguesa optimizado */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsOpen(!isOpen)}
            className="relative px-4 py-2 h-12 rounded-xl bg-deep-200/80 backdrop-blur-sm border border-ocean-500/40 shadow-md hover:bg-deep-200/90 hover:shadow-lg transition-all duration-300 min-w-[85px] group"
            aria-label={isOpen ? "Cerrar menú" : "Abrir menú"}
          >
            <motion.div
              className="flex items-center gap-2"
              animate={isOpen ? { scale: 0.95 } : { scale: 1 }}
              transition={{ duration: 0.2 }}
            >
              {isOpen ? (
                <>
                  <motion.div
                    initial={{ rotate: 0 }}
                    animate={{ rotate: 90 }}
                    transition={{ duration: 0.3 }}
                  >
                    <X className="w-4 h-4 text-pearl-700" />
                  </motion.div>
                  <span className="text-sm font-medium text-pearl-700 font-body">CERRAR</span>
                </>
              ) : (
                <>
                  <motion.div
                    className="flex flex-col gap-0.5"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <motion.div 
                      className="flex gap-1"
                      animate={{ 
                        x: [0, 2, 0],
                      }}
                      transition={{ 
                        duration: 2, 
                        repeat: Infinity, 
                        ease: "easeInOut" 
                      }}
                    >
                      <div className="w-1.5 h-1.5 bg-ocean-500 rounded-full shadow-sm"></div>
                      <div className="w-1.5 h-1.5 bg-jade-500 rounded-full shadow-sm"></div>
                      <div className="w-1.5 h-1.5 bg-ocean-500 rounded-full shadow-sm"></div>
                    </motion.div>
                    <motion.div 
                      className="flex gap-1"
                      animate={{ 
                        x: [0, -2, 0],
                      }}
                      transition={{ 
                        duration: 2, 
                        repeat: Infinity, 
                        ease: "easeInOut",
                        delay: 0.5
                      }}
                    >
                      <div className="w-1.5 h-1.5 bg-jade-500 rounded-full shadow-sm"></div>
                      <div className="w-1.5 h-1.5 bg-ocean-500 rounded-full shadow-sm"></div>
                      <div className="w-1.5 h-1.5 bg-jade-500 rounded-full shadow-sm"></div>
                    </motion.div>
                  </motion.div>
                  <span className="text-sm font-medium text-pearl-700 font-body group-hover:text-ocean-500 transition-colors duration-200">MENÚ</span>
                </>
              )}
            </motion.div>
            
            {/* Efecto de brillo sutil */}
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </Button>
        </motion.div>
      </div>

      {/* Layout desktop - Logo fuera del navbar */}
      <motion.div 
        className="hidden md:block absolute left-4 top-4 z-60 group"
        whileHover={{ scale: 1.08 }}
        transition={{ type: "spring", stiffness: 400, damping: 15 }}
      >
        <Link to="/" className="flex items-center gap-4">
          <div className="relative">
            {/* Logo con efectos de cristal premium - Dark Theme */}
            <div className="relative">
              <div className="w-16 h-16 relative overflow-hidden rounded-3xl shadow-2xl transition-all duration-500 bg-gradient-to-br from-deep-200/80 via-deep-200/70 to-deep-200/60 backdrop-blur-2xl border border-ocean-500/30">
                <img 
                  src="/images/icon.jpg" 
                  alt="Mar de Jade Logo" 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Overlay brillante */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-ocean-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
              
              {/* Anillo luminoso exterior - Dark Theme */}
              <motion.div 
                className="absolute -inset-0.5 rounded-3xl bg-gradient-to-r from-ocean-500/40 via-jade-500/40 to-ocean-500/40 opacity-0 group-hover:opacity-100 blur-sm"
                animate={{
                  background: [
                    "linear-gradient(45deg, rgba(37, 99, 235, 0.4), rgba(8, 145, 178, 0.4), rgba(37, 99, 235, 0.4))",
                    "linear-gradient(45deg, rgba(8, 145, 178, 0.4), rgba(37, 99, 235, 0.4), rgba(8, 145, 178, 0.4))",
                    "linear-gradient(45deg, rgba(37, 99, 235, 0.4), rgba(8, 145, 178, 0.4), rgba(37, 99, 235, 0.4))"
                  ]
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
            
            {/* Burbujas marinas premium - Dark Theme */}
            <motion.div 
              className="absolute -top-2 -right-2 w-3 h-3 rounded-full bg-gradient-to-tr from-ocean-500/50 to-ocean-600/70 shadow-lg"
              animate={{ 
                y: [0, -12, 0],
                opacity: [0.5, 0.8, 0.5],
                scale: [0.8, 1.2, 0.8],
                rotate: [0, 180, 360]
              }}
              transition={{ 
                duration: 3.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.div 
              className="absolute -bottom-2 -left-2 w-2 h-2 rounded-full bg-gradient-to-tr from-jade-500/40 to-jade-600/60 shadow-md"
              animate={{ 
                y: [0, -8, 0],
                opacity: [0.4, 0.7, 0.4],
                scale: [0.6, 1.1, 0.6],
                rotate: [0, -180, -360]
              }}
              transition={{ 
                duration: 4.2,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1.5
              }}
            />
            <motion.div 
              className="absolute top-1 -right-3 w-1.5 h-1.5 rounded-full bg-gradient-to-tr from-ocean-600/35 to-ocean-700/50"
              animate={{ 
                y: [0, -6, 0],
                opacity: [0.35, 0.6, 0.35],
                scale: [0.5, 1, 0.5]
              }}
              transition={{ 
                duration: 2.8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.8
              }}
            />
          </div>
          
          {/* Texto con efectos tipográficos mejorados */}
          <motion.div 
            className="hidden lg:block"
            initial={{ opacity: 0.8 }}
            whileHover={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <motion.h1 
              className="text-2xl font-title font-bold bg-gradient-to-r from-ocean-500 via-ocean-600 to-jade-500 bg-clip-text text-transparent group-hover:from-ocean-600 group-hover:via-jade-500 group-hover:to-ocean-700 transition-all duration-500 text-nav-logo drop-shadow-sm"
              whileHover={{ scale: 1.02 }}
            >
              Mar de Jade
            </motion.h1>
            <motion.p 
              className="text-sm text-pearl-600/90 font-body -mt-1 text-nav-tagline tracking-wide group-hover:text-pearl-700 transition-colors duration-300"
              whileHover={{ x: 2 }}
            >
              Del mar, sin escalas
            </motion.p>
          </motion.div>
        </Link>
      </motion.div>

      {/* Botones de acción premium - lado derecho */}
      <motion.div 
        className="hidden md:flex absolute right-4 top-4 z-60 items-center gap-4"
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
      >
        {/* Espacio para otros botones si los necesitas */}
      </motion.div>

      {/* Navbar elegante centrado - Solo desktop - Dark Theme - Fino y elegante */}
      <motion.nav 
        className={`hidden md:block max-w-3xl mx-auto transition-all duration-700 rounded-2xl border border-ocean-500/20 ${
          scrolled 
            ? 'bg-deep-200/70 backdrop-blur-xl py-1' 
            : 'bg-deep-200/50 backdrop-blur-lg py-1.5'
        }`}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
      >
        {/* Gradiente de fondo sutil - Dark Theme */}
        <div className="absolute inset-0 bg-gradient-to-r from-ocean-500/10 via-transparent to-jade-500/10 opacity-50 rounded-2xl"></div>
        
        <div className="px-4 relative">
          <div className="flex justify-center items-center h-10">
            {/* Navegación premium centrada */}
            <div className="hidden md:flex items-center space-x-2">
              {navItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 + (index * 0.1), ease: "easeOut" }}
                >
                  {item.submenu ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <motion.button 
                          className="relative px-3 py-2 text-nav-primary text-pearl-700 hover:text-ocean-500 transition-all duration-300 rounded-xl hover:bg-deep-300/40 group flex items-center gap-1.5"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          transition={{ duration: 0.2 }}
                        >
                          <span className="font-body relative z-10">{item.label}</span>
                          <ChevronDown className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" />
                        </motion.button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent 
                        className="bg-deep-200/95 backdrop-blur-2xl shadow-2xl border border-ocean-500/20 rounded-2xl p-2 mt-2"
                        style={{
                          backdropFilter: 'blur(40px) saturate(180%)',
                          WebkitBackdropFilter: 'blur(40px) saturate(180%)'
                        }}
                      >
                        {item.submenu.map((subItem, subIndex) => (
                          <DropdownMenuItem key={subIndex} asChild>
                            <motion.div
                              whileHover={{ x: 4 }}
                              transition={{ duration: 0.2 }}
                            >
                              <Link
                                to={subItem.href}
                                className="font-body text-nav-primary text-pearl-700 hover:text-ocean-500 cursor-pointer rounded-xl transition-all duration-300 hover:bg-deep-300/40 p-3 block"
                              >
                                {subItem.label}
                              </Link>
                            </motion.div>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    item.isExternal ? (
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Link
                          to={item.href}
                          className="relative px-3 py-2 text-nav-primary text-pearl-700 hover:text-ocean-500 transition-all duration-300 rounded-xl hover:bg-deep-300/40 group inline-block"
                        >
                          <span className="font-body relative z-10">{item.label}</span>
                          
                          {/* Underline simple */}
                          <motion.div 
                            className="absolute bottom-2 left-1/2 w-0 h-0.5 bg-gradient-to-r from-ocean-500 to-jade-500 rounded-full"
                            whileHover={{ 
                              width: "80%", 
                              x: "-50%" 
                            }}
                            transition={{ duration: 0.3 }}
                          />
                        </Link>
                      </motion.div>
                    ) : (
                      <motion.a 
                        href={item.href} 
                        className="relative px-3 py-2 text-nav-primary text-pearl-700 hover:text-ocean-500 transition-all duration-300 rounded-xl hover:bg-deep-300/40 group"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        transition={{ duration: 0.2 }}
                      >
                        <span className="font-body relative z-10">{item.label}</span>
                        
                        {/* Underline simple */}
                        <motion.div 
                          className="absolute bottom-2 left-1/2 w-0 h-0.5 bg-gradient-to-r from-ocean-500 to-jade-500 rounded-full"
                          whileHover={{ 
                            width: "80%", 
                            x: "-50%" 
                          }}
                          transition={{ duration: 0.3 }}
                        />
                      </motion.a>
                    )
                  )}
                </motion.div>
              ))}
            </div>

          </div>
        </div>
      </motion.nav>

      {/* Menú móvil optimizado y compacto */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Área invisible para cerrar el menú */}
            <div 
              className="md:hidden fixed inset-0 z-[65]"
              onClick={() => {
                setIsOpen(false);
                setOpenSubmenu(null);
              }}
            />
            
            <motion.div 
              className="md:hidden fixed top-14 left-2 right-2 bg-deep-200/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-ocean-500/30 overflow-hidden z-[70]"
              style={{
                backdropFilter: 'blur(12px) saturate(130%)',
                WebkitBackdropFilter: 'blur(12px) saturate(130%)',
                maxHeight: 'calc(100vh - 70px)',
                overflowY: 'auto'
              }}
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
            {/* Gradiente de fondo sutil - Dark Theme */}
            <div className="absolute inset-0 bg-gradient-to-br from-ocean-500/10 via-jade-500/5 to-ocean-500/10"></div>
            
            <div className="p-3 space-y-2 relative">
              {navItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05, ease: "easeOut" }}
                >
                  {item.submenu ? (
                    // Item con submenú - más accesible
                    <div className="space-y-1">
                      <button
                        className="w-full flex items-center justify-between px-4 py-4 text-pearl-700 hover:text-ocean-500 hover:bg-gradient-to-r hover:from-deep-300/40 hover:to-deep-300/30 rounded-xl transition-all duration-200 font-body text-nav-primary backdrop-blur-sm border border-transparent hover:border-ocean-500/20 min-h-[48px]"
                        onClick={() => setOpenSubmenu(openSubmenu === item.label ? null : item.label)}
                      >
                        <span className="relative z-10">{item.label}</span>
                        <motion.div
                          animate={{ rotate: openSubmenu === item.label ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronDown className="w-4 h-4" />
                        </motion.div>
                      </button>
                      
                      <AnimatePresence>
                        {openSubmenu === item.label && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="pl-3 space-y-0.5 overflow-hidden"
                          >
                            {item.submenu.map((subItem, subIndex) => (
                              <motion.div
                                key={subIndex}
                                initial={{ opacity: 0, x: -15 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.2, delay: subIndex * 0.03 }}
                              >
                                <Link
                                  to={subItem.href}
                                  className="block px-4 py-3 text-pearl-600 hover:text-ocean-500 hover:bg-deep-300/30 rounded-lg transition-all duration-150 font-body text-nav-primary min-h-[44px] flex items-center"
                                  onClick={() => {
                                    setIsOpen(false);
                                    setOpenSubmenu(null);
                                  }}
                                >
                                  {subItem.label}
                                </Link>
                              </motion.div>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : item.isExternal ? (
                    <Link
                      to={item.href}
                      className="block relative px-4 py-4 text-pearl-700 hover:text-ocean-500 hover:bg-gradient-to-r hover:from-deep-300/40 hover:to-deep-300/30 rounded-xl transition-all duration-200 font-body text-nav-primary backdrop-blur-sm border border-transparent hover:border-ocean-500/20 min-h-[48px] flex items-center"
                      onClick={() => {
                        setIsOpen(false);
                        setOpenSubmenu(null);
                      }}
                    >
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-ocean-500/10 to-jade-500/10 rounded-xl opacity-0"
                        whileHover={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      />
                      <span className="relative z-10">{item.label}</span>
                      
                      {/* Indicador animado más sutil */}
                      <motion.div
                        className="absolute right-3 top-1/2 w-1.5 h-1.5 bg-gradient-to-r from-ocean-500 to-jade-500 rounded-full"
                        initial={{ scale: 0, y: "-50%" }}
                        whileHover={{ scale: 1 }}
                        transition={{ duration: 0.2 }}
                      />
                    </Link>
                  ) : (
                    <a
                      href={item.href}
                      className="block relative px-4 py-4 text-pearl-700 hover:text-ocean-500 hover:bg-gradient-to-r hover:from-deep-300/40 hover:to-deep-300/30 rounded-xl transition-all duration-200 font-body text-nav-primary backdrop-blur-sm border border-transparent hover:border-ocean-500/20 min-h-[48px] flex items-center"
                      onClick={() => {
                        setIsOpen(false);
                        setOpenSubmenu(null);
                      }}
                    >
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-ocean-500/10 to-jade-500/10 rounded-xl opacity-0"
                        whileHover={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      />
                      <span className="relative z-10">{item.label}</span>
                      
                      {/* Indicador animado más sutil */}
                      <motion.div
                        className="absolute right-3 top-1/2 w-1.5 h-1.5 bg-gradient-to-r from-ocean-500 to-jade-500 rounded-full"
                        initial={{ scale: 0, y: "-50%" }}
                        whileHover={{ scale: 1 }}
                        transition={{ duration: 0.2 }}
                      />
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Overlay simplificado para menú móvil */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="md:hidden fixed inset-0 bg-black/5 z-[25] pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
        )}
      </AnimatePresence>

      {/* WhatsApp Floating Button */}
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        initial={{ opacity: 0, scale: 0, y: 100 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1, type: "spring", stiffness: 300, damping: 25 }}
      >
        <motion.a
          href="https://wa.me/5491160065484"
          target="_blank"
          rel="noopener noreferrer"
          className="relative w-12 h-12 bg-gradient-to-br from-green-500 via-green-600 to-green-500 hover:from-green-600 hover:via-green-700 hover:to-green-600 rounded-full shadow-2xl hover:shadow-green-500/40 flex items-center justify-center transition-all duration-500 border-2 border-white/20 backdrop-blur-sm overflow-visible group"
          whileHover={{ 
            scale: 1.1,
            x: [0, -2, 2, -1, 1, 0],
            y: [0, -1, 1, -2, 2, 0]
          }}
          whileTap={{ scale: 0.95 }}
          transition={{ 
            scale: { duration: 0.3 },
            x: { duration: 0.4, repeat: Infinity, repeatType: "loop" },
            y: { duration: 0.4, repeat: Infinity, repeatType: "loop", delay: 0.1 }
          }}
        >
          {/* Efecto de pulso base */}
          <motion.div
            className="absolute inset-0 rounded-full bg-green-400"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.5, 0, 0.5],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          
          {/* Burbuja oxigenador 1 - Flujo continuo */}
          <motion.div 
            className="absolute -bottom-1 left-1/2 w-2 h-2 rounded-full bg-gradient-to-tr from-green-300/60 to-green-200/80 shadow-md"
            animate={{ 
              y: [0, -25],
              opacity: [0, 0.8, 0.6, 0],
              scale: [0.3, 0.8, 1, 1.2]
            }}
            transition={{ 
              duration: 2.5,
              repeat: Infinity,
              ease: "easeOut",
              delay: 0
            }}
          />
          
          {/* Burbuja oxigenador 2 - Flujo continuo */}
          <motion.div 
            className="absolute -bottom-1 left-1/3 w-1.5 h-1.5 rounded-full bg-gradient-to-tr from-green-400/50 to-green-300/70 shadow-sm"
            animate={{ 
              y: [0, -20],
              opacity: [0, 0.7, 0.5, 0],
              scale: [0.2, 0.6, 0.9, 1.1]
            }}
            transition={{ 
              duration: 2.2,
              repeat: Infinity,
              ease: "easeOut",
              delay: 0.5
            }}
          />
          
          {/* Burbuja oxigenador 3 - Flujo continuo */}
          <motion.div 
            className="absolute -bottom-1 right-1/3 w-1 h-1 rounded-full bg-gradient-to-tr from-green-200/40 to-green-100/60"
            animate={{ 
              y: [0, -15],
              opacity: [0, 0.6, 0.4, 0],
              scale: [0.1, 0.4, 0.7, 0.9]
            }}
            transition={{ 
              duration: 1.8,
              repeat: Infinity,
              ease: "easeOut",
              delay: 1
            }}
          />
          
          {/* Burbuja oxigenador 4 - Flujo continuo */}
          <motion.div 
            className="absolute -bottom-1 right-1/4 w-1.5 h-1.5 rounded-full bg-gradient-to-tr from-green-300/50 to-green-200/70"
            animate={{ 
              y: [0, -18],
              opacity: [0, 0.5, 0.3, 0],
              scale: [0.2, 0.5, 0.8, 1]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeOut",
              delay: 1.5
            }}
          />

          {/* Efecto de brillo rotativo */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
          />
          
          {/* Icono de WhatsApp con vibración - más pequeño */}
          <motion.svg
            className="w-6 h-6 text-white relative z-10"
            fill="currentColor"
            viewBox="0 0 24 24"
            whileHover={{
              x: [0, -1, 1, -1, 1, 0],
              y: [0, 1, -1, 1, -1, 0],
              rotate: [0, -2, 2, -2, 2, 0]
            }}
            transition={{
              duration: 0.5,
              repeat: Infinity,
              repeatType: "loop"
            }}
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488z"/>
          </motion.svg>
        </motion.a>
      </motion.div>
    </motion.div>
  );
};

export default Navigation;
